import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, OFFICIAL_BOOKING_URL } from "@/lib/data";
import { MapPin, ArrowRight } from "lucide-react";
import SectionTitle from "@/components/SectionTitle";
import RotatingVideo from "@/components/RotatingVideo";
import { EXPERIENCES_VIDEOS } from "@/lib/videos";

export default function Experiences() {
    const [exps, setExps] = useState([]);
    const [cat, setCat] = useState("All");

    useEffect(() => { api.get("/experiences").then(({ data }) => setExps(data)).catch(() => {}); }, []);
    const cats = useMemo(() => ["All", ...Array.from(new Set(exps.map((e) => e.category)))], [exps]);
    const items = useMemo(() => cat === "All" ? exps : exps.filter((e) => e.category === cat), [exps, cat]);

    return (
        <div data-testid="experiences-page">
            <RotatingVideo
                videos={EXPERIENCES_VIDEOS}
                eager
                testId="experiences-page-video"
                className="relative w-full h-[92vh] min-h-[640px]"
                overlay={
                    <>
                        <div className="absolute inset-0 bg-gradient-to-t from-navy/75 via-navy/20 to-navy/40" />
                        <div className="absolute inset-0 max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-16 md:pb-24">
                            <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-4 flex items-center gap-3"><span className="w-10 h-[1px] bg-gold" /> Experiences</div>
                            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-ivory max-w-3xl leading-[1.02]">Ocho Rios, <span className="font-serif-italic text-gold">curated</span>.</h1>
                            <p className="mt-6 text-ivory/85 max-w-xl leading-relaxed">From secret waterfalls to reggae sunsets — a private guide to the island beyond our gates.</p>
                            <div className="mt-8 flex items-center gap-3">
                                <a href="#experiences-list" className="btn-gold px-6 py-3 rounded-full">Explore Activities</a>
                                <Link to="/booking" className="btn-outline-ivory px-6 py-3 rounded-full">Plan Your Stay</Link>
                            </div>
                        </div>
                    </>
                }
            />
            <div id="experiences-list" className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-8">
                <div className="flex flex-wrap gap-2">
                    {cats.map((c) => (
                        <button
                            key={c}
                            data-testid={`experiences-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => setCat(c)}
                            className={`text-[11px] uppercase tracking-[0.2em] px-4 py-2 rounded-full border transition-colors ${cat === c ? "bg-navy text-ivory border-navy" : "border-navy/20 text-navy hover:border-gold hover:text-gold"}`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </div>
            <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-32">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map((e) => (
                        <article key={e.id} data-testid={`experience-tile-${e.id}`} className="group hover-lift bg-white rounded-2xl overflow-hidden flex flex-col">
                            <div className="aspect-[4/3] overflow-hidden relative">
                                <img src={e.image} alt={e.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
                                {e.images && e.images.length > 1 && (
                                    <img src={e.images[1]} alt={`${e.name} — second view`} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                                )}
                            </div>
                            <div className="p-6 flex flex-col flex-1">
                                <div className="text-[10px] uppercase tracking-[0.24em] text-gold">{e.category}</div>
                                <h3 className="font-display text-2xl text-navy mt-2">{e.name}</h3>
                                <p className="text-sm text-navy/70 mt-3 leading-relaxed flex-1">{e.description}</p>
                                <div className="mt-4 text-xs text-navy/60 flex items-center gap-3">
                                    <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> {e.distance_km.toFixed(1)} km</span>
                                    <span>· {e.duration}</span>
                                </div>
                                <div className="mt-5 flex items-center gap-2">
                                    <Link to="/booking" data-testid={`experience-page-explore-${e.id}`} className="btn-gold px-4 py-2 rounded-full inline-flex items-center gap-1">Explore Activity <ArrowRight size={11} /></Link>
                                    <Link to="/booking" data-testid={`experience-page-plan-${e.id}`} className="border border-navy text-navy px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.16em] hover:bg-navy hover:text-ivory transition-colors">Plan Your Stay</Link>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </div>
    );
}
